// identity/uuid.js: M3-7 centralized UUID v4 generator (plan-m3 §C7).
//
// crypto.randomUUID() is only exposed in secure contexts (HTTPS, with a
// localhost exception). Multiplayer playtesting on LAN HTTP addresses
// (the common dev path) breaks without a fallback. Build a v4 by hand
// from crypto.getRandomValues() instead, still cryptographically
// random, just hand-assembled. If neither is available, throw, the
// caller should surface a user-visible identity-init error.

let warnedAboutFallback = false;

/**
 * Generate a random UUID v4.
 * @returns {string}
 * @throws {Error} when neither crypto.randomUUID nor crypto.getRandomValues exists
 */
export function generateUUIDv4() {
  /** @type {any} */
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    if (!warnedAboutFallback && typeof console !== 'undefined') {
      console.warn('[identity] crypto.randomUUID() unavailable; using getRandomValues fallback. Serve over HTTPS in production.');
      warnedAboutFallback = true;
    }
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    // Set version (4) and variant (10xx) bits per RFC 4122.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }
  throw new Error('identity-init: no crypto API available for UUID generation');
}

// Exposed for tests that need to reset the once-per-session warning.
export const _internals = {
  resetWarning() { warnedAboutFallback = false; },
};
